/**
 * PATH: apps/qa/src/pages/ai-insights/EvidenceAnalysisResults.tsx
 * PURPOSE: Display AI evidence analysis results with findings cards
 */

import { cn } from '@/lib/utils'
import type { EvidenceAnalysis, AIFinding } from './types'
import {
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
  Loader2,
  FileText,
  Image,
} from 'lucide-react'

// ── Compliance Status Badge ─────────────────────────────────────────────────

function ComplianceStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    compliant: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    review_recommended: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    non_compliant: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  }
  const labels: Record<string, string> = {
    compliant: '✓ Compliant',
    review_recommended: '⚠ Review Recommended',
    non_compliant: '✗ Non-Compliant',
  }
  return (
    <span className={cn('px-3 py-1 rounded-full text-sm font-medium', styles[status] || styles.review_recommended)}>
      {labels[status] || status}
    </span>
  )
}

// ── Finding Card ────────────────────────────────────────────────────────────

function FindingCard({ finding }: { finding: AIFinding }) {
  const severityStyles: Record<string, string> = {
    low: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  }
  const typeIcons = { observation: CheckCircle2, concern: AlertTriangle, recommendation: Lightbulb }
  const Icon = typeIcons[finding.type] || FileText

  return (
    <div className="p-4 hover:bg-accent/50 transition-colors">
      <div className="flex items-start gap-4">
        <div className={cn(
          'p-2 rounded-lg flex-shrink-0',
          finding.type === 'observation' && 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
          finding.type === 'concern' && 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
          finding.type === 'recommendation' && 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
        )}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-foreground capitalize">{finding.type}</span>
            {finding.severity && (
              <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', severityStyles[finding.severity])}>
                {finding.severity}
              </span>
            )}
            <span className="text-xs text-muted-foreground ml-auto">
              {Math.round(finding.confidence * 100)}% confidence
            </span>
          </div>
          <p className="text-sm text-foreground">{finding.description}</p>
          {finding.location && (
            <p className="text-xs text-muted-foreground mt-1">📍 {finding.location}</p>
          )}
          {finding.recommendation && (
            <div className="mt-2 p-2 rounded-lg bg-violet-50 dark:bg-violet-950/30 text-sm">
              <span className="font-medium text-violet-700 dark:text-violet-400">Recommendation: </span>
              <span className="text-violet-600 dark:text-violet-300">{finding.recommendation}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Main Component ──────────────────────────────────────────────────────────

export function EvidenceAnalysisResults({
  data,
  isLoading,
  error,
}: {
  data?: EvidenceAnalysis
  isLoading: boolean
  error: Error | null
}) {
  if (isLoading) {
    return (
      <div className="bg-card rounded-xl border border-border p-8 flex flex-col items-center justify-center min-h-[400px]">
        <div className="relative">
          <div className="w-16 h-16 rounded-full bg-violet-100 dark:bg-violet-900/30 animate-pulse" />
          <Loader2 className="w-8 h-8 text-violet-500 animate-spin absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        <p className="mt-4 text-foreground font-medium">Analyzing evidence...</p>
        <p className="text-sm text-muted-foreground">AI is reviewing photos for quality issues</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-card rounded-xl border border-destructive/50 p-8 text-center min-h-[400px] flex flex-col items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
          <AlertTriangle className="w-8 h-8 text-destructive" />
        </div>
        <p className="font-medium text-foreground">Analysis Failed</p>
        <p className="text-sm text-muted-foreground mt-1">{error.message || 'Please try again'}</p>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="bg-card rounded-xl border border-border border-dashed p-8 text-center min-h-[400px] flex flex-col items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <Image className="w-8 h-8 text-muted-foreground" />
        </div>
        <p className="font-medium text-foreground">No Analysis Yet</p>
        <p className="text-sm text-muted-foreground mt-1">
          Select an inspection and click "Run Analysis" to get AI insights
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <FileText className="w-4 h-4 text-violet-500" />
              Analysis Summary
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Processed in {data.processing_time_ms}ms • {Math.round(data.confidence * 100)}% confidence
            </p>
          </div>
          {data.compliance_check && <ComplianceStatusBadge status={data.compliance_check.status} />}
        </div>
        <p className="text-foreground">{data.summary}</p>
        {data.compliance_check && (
          <div className="mt-4 p-4 rounded-lg bg-muted/50">
            <p className="text-sm font-medium text-foreground">Standard: {data.compliance_check.standard}</p>
            <p className="text-sm text-muted-foreground mt-1">{data.compliance_check.notes}</p>
          </div>
        )}
      </div>
      {data.findings.length > 0 && (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold text-foreground">Findings ({data.findings.length})</h3>
          </div>
          <div className="divide-y divide-border">
            {data.findings.map((finding, index) => (
              <FindingCard key={index} finding={finding} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
